import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAdmin } from "../../App";
import {
  LayoutDashboard,
  Building2,
  MessageSquare,
  LogOut,
  ShieldCheck,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Star,
  ChevronLeft,
  ChevronRight,
  FileText,
  BookOpen,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";

const AdminBlogList = () => {
  const { admin, adminLogout, adminToken } = useAdmin();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [publishedFilter, setPublishedFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [deleteArticle, setDeleteArticle] = useState(null);
  const limit = 10;

  useEffect(() => {
    fetchArticles();
    fetchStats();
  }, [adminToken, page, category, publishedFilter]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category && category !== "all") params.append("category", category);
      if (publishedFilter === "published") params.append("published", "true");
      if (publishedFilter === "draft") params.append("published", "false");
      if (search) params.append("search", search);
      params.append("limit", limit);
      params.append("skip", page * limit);

      const response = await axios.get(`${API}/admin/blog/articles?${params.toString()}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setArticles(response.data.articles);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching articles:", error);
      toast.error("Failed to fetch articles");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/blog/stats`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchArticles();
  };

  const handleDelete = async () => {
    if (!deleteArticle) return;
    
    try {
      await axios.delete(`${API}/admin/blog/articles/${deleteArticle.article_id}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      toast.success("Article deleted successfully");
      setDeleteArticle(null);
      fetchArticles();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete article");
    }
  };

  const handleLogout = () => {
    adminLogout();
    navigate("/admin");
  };

  const sidebarItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
    { icon: Building2, label: "Properties", path: "/admin/properties" },
    { icon: FileText, label: "Blog", path: "/admin/blog", active: true },
    { icon: MessageSquare, label: "Inquiries", path: "/admin/inquiries" },
  ];

  const categories = [
    { value: "rera", label: "RERA & Compliance" },
    { value: "buying_guide", label: "Buying Guide" },
    { value: "finance", label: "Home Finance" },
    { value: "market_trends", label: "Market Trends" },
    { value: "legal", label: "Legal & Documentation" },
  ];

  const getCategoryLabel = (slug) => {
    return categories.find(c => c.value === slug)?.label || slug;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-stone-900 flex" data-testid="admin-blog-list">
      {/* Sidebar */}
      <aside className="w-64 bg-stone-800 border-r border-stone-700 flex flex-col">
        <div className="p-6 border-b border-stone-700">
          <Link to="/admin/dashboard" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-amber-500 rounded-sm flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-display text-lg text-white">RERA Admin</div>
              <div className="text-xs text-stone-400">Control Panel</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {sidebarItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    item.active
                      ? "bg-amber-500/10 text-amber-500 border border-amber-500/20"
                      : "text-stone-400 hover:bg-stone-700 hover:text-white"
                  }`}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-stone-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-medium">
              {admin?.name?.charAt(0) || "A"}
            </div>
            <div>
              <p className="text-white text-sm font-medium">{admin?.name}</p>
              <p className="text-stone-400 text-xs">{admin?.email}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-stone-600 text-stone-400 hover:bg-stone-700 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <header className="bg-stone-800 border-b border-stone-700 px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl text-white">Blog Articles</h1>
              <p className="text-stone-400 text-sm">Manage your blog content and SEO</p>
            </div>
            <Link to="/admin/blog/new">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-article-btn">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                New Article
              </Button>
            </Link>
          </div>
        </header>

        {/* Stats Cards */}
        {stats && (
          <div className="px-8 py-6 grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-stone-800 border border-stone-700 p-4 rounded-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-900/30 rounded-sm flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-display text-white">{stats.total_articles}</p>
                  <p className="text-xs text-stone-400">Total Articles</p>
                </div>
              </div>
            </div>
            <div className="bg-stone-800 border border-stone-700 p-4 rounded-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-900/30 rounded-sm flex items-center justify-center">
                  <Eye className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-display text-white">{stats.published_articles}</p>
                  <p className="text-xs text-stone-400">Published</p>
                </div>
              </div>
            </div>
            <div className="bg-stone-800 border border-stone-700 p-4 rounded-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-900/30 rounded-sm flex items-center justify-center">
                  <EyeOff className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-display text-white">{stats.draft_articles}</p>
                  <p className="text-xs text-stone-400">Drafts</p>
                </div>
              </div>
            </div>
            <div className="bg-stone-800 border border-stone-700 p-4 rounded-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-900/30 rounded-sm flex items-center justify-center">
                  <Star className="w-5 h-5 text-purple-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-display text-white">{stats.featured_articles}</p>
                  <p className="text-xs text-stone-400">Featured</p>
                </div>
              </div>
            </div>
            <div className="bg-stone-800 border border-stone-700 p-4 rounded-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-900/30 rounded-sm flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-display text-white">{stats.total_views?.toLocaleString()}</p>
                  <p className="text-xs text-stone-400">Total Views</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="px-8 pb-4">
          <div className="bg-stone-800 border border-stone-700 p-4 rounded-sm">
            <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" strokeWidth={1.5} />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search articles..."
                    className="pl-10 bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                </div>
              </div>
              <Select value={category} onValueChange={(v) => { setCategory(v); setPage(0); }}>
                <SelectTrigger className="w-48 bg-stone-700 border-stone-600 text-white">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={publishedFilter} onValueChange={(v) => { setPublishedFilter(v); setPage(0); }}>
                <SelectTrigger className="w-40 bg-stone-700 border-stone-600 text-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Drafts</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white">
                Search
              </Button>
            </form>
          </div>
        </div>

        {/* Articles List */}
        <div className="px-8 pb-8">
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="bg-stone-800 border border-stone-700 p-6 rounded-sm animate-pulse">
                  <div className="flex gap-4">
                    <div className="w-32 h-20 bg-stone-700 rounded" />
                    <div className="flex-1">
                      <div className="h-5 bg-stone-700 rounded w-1/2 mb-2" />
                      <div className="h-4 bg-stone-700 rounded w-3/4" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : articles.length === 0 ? (
            <div className="bg-stone-800 border border-stone-700 p-12 rounded-sm text-center">
              <FileText className="w-12 h-12 text-stone-600 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-display text-lg text-white mb-2">No Articles Found</h3>
              <p className="text-stone-400 mb-6">
                {search || category !== "all" || publishedFilter !== "all"
                  ? "No articles match your filters."
                  : "Start by creating your first blog article."}
              </p>
              <Link to="/admin/blog/new">
                <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                  <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Create Article
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {articles.map((article) => (
                <div
                  key={article.article_id}
                  className="bg-stone-800 border border-stone-700 p-4 rounded-sm hover:border-stone-600 transition-colors"
                  data-testid="blog-article-row"
                >
                  <div className="flex gap-4">
                    {/* Thumbnail */}
                    <div className="w-40 h-24 flex-shrink-0 rounded overflow-hidden bg-stone-700">
                      {article.featured_image ? (
                        <img
                          src={article.featured_image}
                          alt={article.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FileText className="w-8 h-8 text-stone-600" strokeWidth={1.5} />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="px-2 py-0.5 bg-stone-700 text-stone-300 text-xs rounded">
                              {getCategoryLabel(article.category)}
                            </span>
                            {article.featured && (
                              <span className="px-2 py-0.5 bg-amber-900/30 text-amber-400 text-xs rounded flex items-center gap-1">
                                <Star className="w-3 h-3" strokeWidth={1.5} /> Featured
                              </span>
                            )}
                            {article.published ? (
                              <span className="px-2 py-0.5 bg-emerald-900/30 text-emerald-400 text-xs rounded">
                                Published
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 bg-stone-600 text-stone-300 text-xs rounded">
                                Draft
                              </span>
                            )}
                          </div>
                          <h3 className="font-medium text-white truncate">{article.title}</h3>
                          <p className="text-stone-400 text-sm truncate">{article.excerpt}</p>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Link to={`/admin/blog/edit/${article.article_id}`}>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-stone-600 text-stone-400 hover:bg-stone-700 hover:text-white"
                            >
                              <Edit className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                          </Link>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteArticle(article)}
                            className="border-stone-600 text-stone-400 hover:bg-red-900/30 hover:text-red-400 hover:border-red-800"
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                        </div>
                      </div>

                      {/* Meta */}
                      <div className="flex items-center gap-4 mt-2 text-xs text-stone-500">
                        <span>By {article.author}</span>
                        <span>•</span>
                        <span>{article.read_time} min read</span>
                        <span>•</span>
                        <span>{article.views?.toLocaleString()} views</span>
                        <span>•</span>
                        <span>{formatDate(article.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between bg-stone-800 border border-stone-700 px-6 py-4 rounded-sm">
                  <p className="text-stone-400 text-sm">
                    Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, page - 1))}
                      disabled={page === 0}
                      className="border-stone-600 text-stone-400 hover:bg-stone-700"
                    >
                      <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <span className="text-stone-400 text-sm">
                      Page {page + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                      disabled={page >= totalPages - 1}
                      className="border-stone-600 text-stone-400 hover:bg-stone-700"
                    >
                      <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteArticle} onOpenChange={() => setDeleteArticle(null)}>
        <AlertDialogContent className="bg-stone-800 border-stone-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Article</AlertDialogTitle>
            <AlertDialogDescription className="text-stone-400">
              Are you sure you want to delete this article? This action cannot be undone.
              <br /><br />
              <strong className="text-white">{deleteArticle?.title}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-stone-700 text-white hover:bg-stone-600 border-stone-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminBlogList;
